import { create } from "zustand";
import { StringsKeys } from "@/core/internationalization/strings";
import Exception from "@/core/exceptions/Exception";
import ExceptionService from "@/core/exceptions/exceptionService/ExceptionService";
import DefaultExceptionTypes from "@/core/exceptions/DefaultExceptionTypes";
import {
  CustomerAnalysisResponse,
  KYCAnalysis,
  RiskAnalysis,
  ScreeningReport,
} from "@/core/customers/entities/CustomerAnalysis";
import {
  createDefaultCustomerAnalysisResponse,
  createDefaultKYCAnalysis,
  createDefaultRiskAnalysis,
} from "@/core/customers/entities/defaults";
import GetCustomerAnalysisUseCase from "@/core/customers/useCases/GetCustomerAnalysisUseCase/GetCustomerAnalysisUseCase";
import CustomerAnalysisRepositoryMock from "@/details/data/backend/rest/mock/CustomerAnalysisRepositoryMock";

const customerAnalysisRepositoryMock = new CustomerAnalysisRepositoryMock();

interface CustomerAnalysisState {
  data: CustomerAnalysisResponse;
  kycAnalysis: KYCAnalysis | null;
  riskAnalysis: RiskAnalysis | null;
  screeningReports: ScreeningReport[];
  setKycAnalysis: (kycAnalysis: KYCAnalysis) => void;
  setRiskAnalysis: (riskAnalysis: RiskAnalysis) => void;
  setScreeningReports: (screeningReports: ScreeningReport[]) => void;
  loading: boolean;
  failedFetchingCustomerAnalysisException: Exception | null;
  setFailedFetchingAnalysisException: (
    failedFetchingCustomerAnalysisException: Exception | null
  ) => void;
  fetchAnalysis: (
    translate: (key: StringsKeys) => string,
    businessKey: string
  ) => Promise<void>;
  resetAnalysis: () => void;
}

export const useCustomerAnalysisStore = create<CustomerAnalysisState>(
  (set) => ({
    data: createDefaultCustomerAnalysisResponse(),
    kycAnalysis: createDefaultKYCAnalysis(),
    riskAnalysis: createDefaultRiskAnalysis(),
    screeningReports: [],
    setKycAnalysis: (kycAnalysis) => set({ kycAnalysis }),
    setRiskAnalysis: (riskAnalysis) => set({ riskAnalysis }),
    setScreeningReports: (screeningReports) => set({ screeningReports }),
    loading: false,
    failedFetchingCustomerAnalysisException: null,
    setFailedFetchingAnalysisException: (
      failedFetchingCustomerAnalysisException
    ) => set({ failedFetchingCustomerAnalysisException }),
    fetchAnalysis: async (translate, businessKey) => {
      try {
        set({ loading: true });

        const item = await new GetCustomerAnalysisUseCase(
          customerAnalysisRepositoryMock
        ).execute({ businessKey });
        set({
          data: item,
          kycAnalysis: item?.data?.kycAnalysis,
          riskAnalysis: item?.data?.riskAnalysis,
          screeningReports: item?.data?.screeningReports || [],
          failedFetchingCustomerAnalysisException: null,
        });
      } catch (error) {
        const exception = ExceptionService.handleException<
          undefined,
          DefaultExceptionTypes
        >(
          error as Exception<undefined, DefaultExceptionTypes>,
          translate,
          (exceptionType: DefaultExceptionTypes) => {
            if (exceptionType === DefaultExceptionTypes.NETWORK_ERROR) {
              return translate(StringsKeys.networkError);
            }
            return null;
          }
        );
        console.error(
          "Erreur lors du chargement de l'analyse client :",
          exception
        );
        set({
          kycAnalysis: null,
          riskAnalysis: null,
          screeningReports: [],
          failedFetchingCustomerAnalysisException: exception,
        });
      } finally {
        set({ loading: false });
      }
    },
    resetAnalysis: () => {
      set({
        data: createDefaultCustomerAnalysisResponse(),
        kycAnalysis: createDefaultKYCAnalysis(),
        riskAnalysis: createDefaultRiskAnalysis(),
        screeningReports: [],
        loading: false,
        failedFetchingCustomerAnalysisException: null,
      });
    },
  })
);
